import { EditOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  message,
  Switch,
  Table,
  TablePaginationConfig,
  Tag,
  Tooltip,
} from "antd";
import { useEffect, useState } from "react";
import apiService from "../../../services/apiService";
import AddNewUser from "./AddNewUser";
import styles from "../manageRole.module.css";

interface Team {
  id: number;
  team_name: string;
}

interface Role {
  id: number;
  role_name: string;
  team: Team | null;
}

export interface User {
  roleId: string | undefined;
  reportingManagerId: string | undefined;
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  mobile: number;
  status: boolean;
  createdAt: string;
  updatedAt: string;
  role: Role;
  reportingManagers: string[];
}

interface Pagination {
  currentPage: number;
  pageSize: number;
  totalUsers: number;
  totalPages: number;
}

export interface UsersResponse {
  message: string;
  users: User[];
  pagination: Pagination;
}

interface TableParams {
  pagination: TablePaginationConfig;
}

const UsersTab = ({ searchText, refreshKey = 0 }: { searchText: string; refreshKey?: number }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [addNewUserModal, setAddNewUserModal] = useState(false);
  const [userId, setUserId] = useState<string | number>();

  const [debouncedSearch, setDebouncedSearch] = useState(searchText);

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
      showSizeChanger: true,
      pageSizeOptions: ["10", "20", "50", "100"],
      position: ["bottomRight"],
      showTotal: (total: number, range: number[]) =>
        `Showing ${range[0]} - ${range[1]} of ${total} results`,
    },
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchText]);

  const getUser = () => {
    // setUsers(user)
    fetchUsers(
      tableParams.pagination.current || 1,
      tableParams.pagination.pageSize || 10,
      debouncedSearch
    );
  };

  const [loading, setLoading] = useState(false);
  const closeUserModel = () => {
    setAddNewUserModal(false);
  };
  const openModal = (id: string | number) => {
    setAddNewUserModal(true);
    setUserId(id);
  };

  const fetchUsers = async (page = 1, pageSize = 10, query = "") => {
    setLoading(true);
    try {
      const response: UsersResponse = await apiService.get(
        `/users?page=${page}&pageSize=${pageSize}&search=${query}`
      );
      if (response.users) {
        setUsers(response.users);
        setTableParams((prev) => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            current: response.pagination.currentPage,
            pageSize: response.pagination.pageSize,
            total: response.pagination.totalUsers,
            showTotal: (total, range) =>
              `Showing ${range[0]} - ${range[1]} of ${total} results`,
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(
      tableParams.pagination.current || 1,
      tableParams.pagination.pageSize || 10,
      debouncedSearch
    );
  }, [debouncedSearch, refreshKey]);

  const handleStatusToggle = async (userId: number, isActive: boolean) => {
    try {
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, status: isActive } : user
        )
      );
      const response: any = await apiService.put(
        `/users/update-status/${userId}`,
        { isActive }
      );
      if (response?.message === "User status updated successfully.") {
        message.success(
          `${response.user.firstName} ${response.user.lastName} is now ${
            isActive ? "Active" : "Inactive"
          }`
        );
      }
    } catch (error) {
      console.error("Error updating status:", error);
      message.error("Failed to update user status.");
    }
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setTableParams((prev) => ({
      ...prev,
      pagination: {
        ...pagination,
        position: ["bottomRight"],
        showTotal: (total: number, range: number[]) =>
          `Showing ${range[0]} - ${range[1]} of ${total} results`,
      },
    }));
    fetchUsers(pagination.current || 1, pagination.pageSize || 10);
  };

  const getInitials = (firstName: string, lastName: string) => {
    const first = firstName?.trim()?.[0] || "";
    const last = lastName?.trim()?.[0] || "";
    return `${first}${last}`.toUpperCase() || "U";
  };

  const columns = [
    {
      title: "NAME",
      dataIndex: "name",
      key: "name",
      render: (_: any, record: User) => (
        <div className={styles.personCell}>
          <div className={styles.personAvatar}>
            {getInitials(record.firstName, record.lastName)}
          </div>
          <div>
            <div className={styles.personName}>
              {record.firstName} {record.lastName}
            </div>
            <div className={styles.personEmail}>{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Mobile Number",
      dataIndex: "mobile",
      key: "mobile",
      render: (mobile: number) => <span className={styles.mutedCell}>{mobile}</span>,
    },
    {
      title: "ROLE",
      dataIndex: "role",
      key: "role",
      render: (_: any, record: User) => (
        <span className={styles.primaryCell}>{record.role.role_name}</span>
      ),
    },
    {
      title: "TEAM",
      dataIndex: "team",
      key: "team",
      render: (_: any, record: User) => {
        const teamName = record.role.team?.team_name;
        if (!teamName) {
          return <span className={styles.mutedCell}>-</span>;
        }

        const normalized = teamName.toLowerCase();
        const teamClass = normalized.includes("partner")
          ? styles.teamTagPink
          : normalized.includes("super")
          ? styles.teamTagTeal
          : styles.teamTagPurple;

        return <Tag className={`${styles.teamTag} ${teamClass}`}>{teamName}</Tag>;
      },
    },
    {
      title: "REPORTING MANAGER",
      dataIndex: "reportingManagers",
      key: "reportingManagers",
      render: (_: any, record: User) =>
        record.reportingManagers?.length ? (
          <span className={styles.primaryCell}>
            {record.reportingManagers.join(", ")}
          </span>
        ) : (
          <span className={styles.mutedCell}>-</span>
        ),
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      render: (_: any, record: User) => {
        return (
          <Switch
            checked={record.status}
            onChange={(checked) => handleStatusToggle(record.id, checked)}
            className={styles.statusSwitch}
          />
        );
      },
    },
    {
      title: "ACTION",
      key: "action",
      render: (_: any, record: User) => (
        <Tooltip title="Edit">
          <Button
            type="text"
            className={styles.editButton}
            onClick={() => openModal(record.id)}
            icon={<EditOutlined />}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Col className={styles.tableColumn}>
        <Card className={styles.tableCard} bodyStyle={{ padding: 0 }}>
          <Table
            key={users.length}
            columns={columns}
            rowKey="id"
            dataSource={users}
            loading={loading}
            pagination={tableParams.pagination}
            onChange={handleTableChange}
            className={styles.manageTable}
            rowClassName={() => styles.tableRow}
            scroll={{ x: 980 }}
          />
        </Card>
      </Col>
      <AddNewUser
        getUser={getUser}
        isEdit={true}
        id={userId}
        addNewUserModal={addNewUserModal}
        closeModel={closeUserModel}
      />
    </>
  );
};

export default UsersTab;
